// deps
import React, { ReactElement, useCallback } from "react";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";

// constants
import { TEXTS } from "@payment-ui/constants/index";
import { DISCOUNT_BADGE_ICON } from "../constants";

// components
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";

// test-ids
import { ADD_NEW_CARD_TEST_IDS } from "@swiggy-private/payment-test-ids";

import styles from "../AddNewCard.v4.scss";
import { format, isEmpty } from "@swiggy-private/core-ui";

import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";

import { default as info } from "@swiggy-private/dls-web-icons/dist/web/generic/info/12";

interface ProceedButtonProps {
    secureCard: boolean;
    juspayTokenizationMeta: boolean;
    ctaEnabled: boolean;
    showCardNumberErrorMessage: boolean;
    onPayClick: (secureCardVal: boolean) => void;
    showSecureCardHalfCardWithActions: (primaryAction: VoidFunction, secondaryAction: VoidFunction) => void;
    onProceedToPayClickAction?: (secureCard: boolean, discountText: string) => void;
    payableAmount?: string;
    inProgress?: boolean;
    rewardApplicable?: boolean;
    discountText?: string;
    saveCardCtaHelperText?: string;
}

const ProceedButton = ({
    secureCard,
    juspayTokenizationMeta,
    ctaEnabled,
    showCardNumberErrorMessage,
    onPayClick,
    showSecureCardHalfCardWithActions,
    onProceedToPayClickAction,
    payableAmount,
    inProgress,
    rewardApplicable = true,
    discountText = "",
    saveCardCtaHelperText,
}: ProceedButtonProps): ReactElement => {
    const onlySecureCardChecked = useCallback(() => onPayClick(true), [onPayClick]);
    const neitherOptionSelected = useCallback(() => onPayClick(false), [onPayClick]);

    const onProceedClick = useCallback(
        (e: React.MouseEvent): void => {
            e.preventDefault();
            typeof onProceedToPayClickAction === "function" && onProceedToPayClickAction(secureCard, discountText);
            if (!secureCard) {
                if (!juspayTokenizationMeta) {
                    onPayClick(secureCard);
                    return;
                }
                showSecureCardHalfCardWithActions(onlySecureCardChecked, neitherOptionSelected);
            } else {
                onlySecureCardChecked();
            }
        },
        [
            secureCard,
            juspayTokenizationMeta,
            onProceedToPayClickAction,
            showSecureCardHalfCardWithActions,
            onlySecureCardChecked,
            neitherOptionSelected,
            onPayClick,
            discountText,
        ],
    );

    let buttonText = !isEmpty(payableAmount) ? format(TEXTS.PROCEED_TO_PAY_AMOUNT, payableAmount) : TEXTS.PROCEED;
    // If Rewards not applicable
    if (!rewardApplicable) buttonText = TEXTS.PROCEED_WITHOUT_OFFER;

    return (
        <div className={styles.newCardSaveContainer} data-addnewcard-proceed>
            {!isEmpty(discountText) && (
                <div className={styles.newCardDiscountTextContainer}>
                    <Image cloudinaryId={DISCOUNT_BADGE_ICON} height="16px" width="16px" alt="" />
                    <Text variant="V2.body3.neutral.regular" color="positive">
                        {discountText}
                    </Text>
                </div>
            )}
            {!isEmpty(saveCardCtaHelperText) && (
                <div className={styles.newCardSaveCtaHelper} data-testid={ADD_NEW_CARD_TEST_IDS.SAVE_CARD_CTA_HELPER}>
                    <GenericIcon
                        className={styles.newCardSaveCtaHelperIcon}
                        Icon={info}
                        strokeColor="text_Low_Emphasis"
                        fillColor="text_Low_Emphasis"
                    />
                    <Text variant="V2.overline.neutral.medium" color="text_Low_Emphasis">
                        {saveCardCtaHelperText}
                    </Text>
                </div>
            )}
            <SuperButton
                buttonType="primary"
                loading={inProgress}
                onClick={onProceedClick}
                data-testid={ADD_NEW_CARD_TEST_IDS.PROCEED_TO_PAYMENT_BUTTON}
                isDisabled={showCardNumberErrorMessage || !ctaEnabled}
                variant="fullwidth"
                buttonSize="large"
            >
                {buttonText}
            </SuperButton>
        </div>
    );
};

export default React.memo(ProceedButton);
